/**
 * Produtos de radar do SIPAM exibidos pelo AmaView e as paletas OFICIAIS de
 * cada um.
 *
 * As cores são as do estilo publicado no GeoServer do SIPAM
 * (GetLegendGraphic em JSON dos estilos `sipam:max_dbz` e `sipam:sbbe_rate`)
 * e batem pixel a pixel com os PNGs — levantamento do script `get_sipam.py`
 * do autor. As classes NÃO são uniformes: são exatamente as da legenda.
 *
 * O que cada faixa quer dizer em linguagem simples é nosso (as `leituras`),
 * seguindo a convenção usual de radar meteorológico: a refletividade cresce
 * com o tamanho e a quantidade das gotas, e acima de ~50 dBZ o eco costuma
 * vir de granizo.
 */

export type Produto = 'dbz' | 'rate';

export const PRODUTOS: readonly Produto[] = ['dbz', 'rate'];

export interface Classe {
  /** Cor exata do PNG, `#rrggbb`. */
  cor: string;
  /** Faixa da classe [min, max), na unidade do produto. */
  min: number;
  max: number;
}

/** Faixa de leitura em linguagem simples, cobrindo classes consecutivas. */
export interface Leitura {
  /** Índice da primeira classe (em `classes`) desta leitura. */
  de: number;
  /** Índice da última classe (inclusivo). */
  ate: number;
  texto: string;
}

export interface DefProduto {
  id: Produto;
  /** Nome curto (seletor). */
  nome: string;
  /** Nome completo (legenda, exportação). */
  titulo: string;
  unidade: string;
  classes: readonly Classe[];
  leituras: readonly Leitura[];
  /** Uma frase de interpretação. */
  dica: string;
}

const c = (hex: string, min: number, max: number): Classe => ({ cor: `#${hex.toLowerCase()}`, min, max });

export const DBZ: DefProduto = {
  id: 'dbz',
  nome: 'Refletividade',
  titulo: 'Refletividade máxima (dBZ)',
  unidade: 'dBZ',
  classes: [
    c('E1E5E8', -30, -15),
    c('66EF34', -15, 0),
    c('FCF36B', 0, 5),
    c('20E9F6', 5, 10),
    c('11D4E9', 10, 15),
    c('2C7FF3', 15, 25),
    c('2C2DF3', 25, 30),
    c('4EFB07', 30, 33),
    c('40CB05', 33, 36),
    c('2E5517', 36, 39),
    c('FCEC00', 39, 42),
    c('FC772A', 42, 45),
    c('FC4F46', 45, 48),
    c('F42608', 48, 51),
    c('E5439F', 51, 54),
    c('C82A65', 54, 57),
    c('F51F60', 57, 60),
    c('FF00CA', 60, 65),
    c('FF6AE2', 65, 70),
    c('FB00FF', 70, 80),
    c('BE4BEF', 80, 85),
  ],
  leituras: [
    { de: 0, ate: 4, texto: 'eco fraco' },
    { de: 5, ate: 6, texto: 'chuva fraca' },
    { de: 7, ate: 9, texto: 'moderada' },
    { de: 10, ate: 13, texto: 'forte' },
    { de: 14, ate: 16, texto: 'muito forte' },
    { de: 17, ate: 20, texto: 'granizo provável' },
  ],
  dica:
    'Quanto mais água (e maiores as gotas) no ar, mais forte o eco. Abaixo de 15 dBZ costuma ser garoa, nuvem ou ' +
    'até insetos; acima de 50, chuva torrencial e possível granizo.',
};

export const RATE: DefProduto = {
  id: 'rate',
  nome: 'Chuva (mm/h)',
  titulo: 'Taxa de chuva estimada (mm/h)',
  unidade: 'mm/h',
  classes: [
    c('5B3F94', 0, 1),
    c('260080', 1, 2),
    c('0259F4', 2, 4),
    c('3B33B8', 4, 6),
    c('79CBDD', 6, 8),
    c('376E19', 8, 10),
    c('57A227', 10, 13),
    c('BADD69', 13, 16),
    c('F1CF16', 16, 20),
    c('F6F262', 20, 25),
    c('FF9865', 25, 30),
    c('E89F29', 30, 40),
    c('B12079', 40, 50),
    c('E5521C', 50, 60),
    c('F71A05', 60, 80),
    c('B3BBC5', 80, 100),
  ],
  leituras: [
    { de: 0, ate: 1, texto: 'fraca' },
    { de: 2, ate: 5, texto: 'moderada' },
    { de: 6, ate: 12, texto: 'forte' },
    { de: 13, ate: 15, texto: 'muito forte' },
  ],
  dica:
    'Milímetros de chuva por hora, estimados pelo SIPAM a partir do eco do radar — não é medida de pluviômetro. ' +
    'Acima de 10 mm/h já é chuva forte; acima de 50, temporal.',
};

export const DEF_PRODUTO: Record<Produto, DefProduto> = { dbz: DBZ, rate: RATE };

/**
 * Belém (sbbe) é outro sistema e publica com OUTRA paleta: as mesmas classes,
 * com as cores da legenda do visualizador oficial
 * (`hidro.sipam.gov.br/assets/img/legenda/legenda-radar-dbz.svg` e
 * `…-preciptacao.svg`) — casamento de 100% dos pixels opacos em 38 arquivos
 * de cada produto. Os outros oito radares usam a paleta do GeoServer (acima),
 * também com 100% de casamento.
 *
 * Uma cor, uma chuva: ao reprojetar, a cor de cada classe de Belém é trocada
 * pela cor da MESMA classe na paleta dos outros radares, para que a legenda
 * valha no mapa inteiro. É troca cor a cor, classe por classe — nenhum valor
 * muda. O clique lê o PNG original com a paleta dele.
 *
 * Cores na ordem das classes de `DEF_PRODUTO[produto].classes`.
 */
export const PALETAS_PROPRIAS: Readonly<Record<string, Readonly<Record<Produto, readonly string[]>>>> = {
  sbbe: {
    dbz: [
      '#c0c0c0', '#9ccd3a', '#ede78f', '#55fdfe', '#46bdfd', '#5067df', '#3000fd', '#46fc1f', '#36ca16', '#166306',
      '#fcff29', '#d4a72c', '#f28474', '#e43a32', '#c76390', '#862752', '#fccbfc', '#fbacfe', '#b113b5', '#aa55fd',
      '#8104c1',
    ],
    rate: [
      '#4b3c8a', '#13007f', '#3000fd', '#2500cb', '#91cdea', '#166306', '#318a26', '#9ccd3a', '#f9d824', '#fcff29',
      '#f68355', '#f7a81f', '#c02485', '#f54f19', '#f42717', '#c0c0c0',
    ],
  },
};

/** Cores das classes como o radar `id` as grava no PNG. */
export function coresDoRadar(id: string, produto: Produto): readonly string[] {
  return PALETAS_PROPRIAS[id]?.[produto] ?? DEF_PRODUTO[produto].classes.map((k) => k.cor);
}

export function isProduto(x: unknown): x is Produto {
  return x === 'dbz' || x === 'rate';
}

/** "15–25 dBZ", "80–100 mm/h". */
export function rotuloClasse(def: DefProduto, i: number): string {
  const k = def.classes[i];
  const fmt = (n: number) => String(n).replace('-', '−');
  return `${fmt(k.min)}–${fmt(k.max)} ${def.unidade}`;
}

function rgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/**
 * Classe de uma cor lida do PNG. Casamento EXATO primeiro (o PNG original é
 * classificado); sem casamento exato, a cor mais próxima até `tolerancia`
 * (distância euclidiana em RGB) — cobre bitmaps reduzidos, onde a borda de
 * uma mancha mistura cores. Longe de tudo: null.
 */
export function classeDaCor(
  def: DefProduto,
  r: number,
  g: number,
  b: number,
  tolerancia = 24,
  cores: readonly string[] = def.classes.map((k) => k.cor),
): number | null {
  let melhor = -1;
  let dist = Infinity;
  cores.forEach((cor, i) => {
    const [kr, kg, kb] = rgb(cor);
    const d = Math.hypot(kr - r, kg - g, kb - b);
    if (d < dist) {
      dist = d;
      melhor = i;
    }
  });
  return melhor >= 0 && dist <= tolerancia ? melhor : null;
}

/** RGBA empacotado como o `Uint32Array` sobre os bytes de um `ImageData` o lê (independe da ordem de bytes). */
export function empacotar(r: number, g: number, b: number, a = 255): number {
  const bytes = new Uint8Array([r, g, b, a]);
  return new Uint32Array(bytes.buffer)[0];
}

/**
 * Tabela de troca de cores (pares [de, para] empacotados) para levar a paleta
 * própria de um radar à paleta comum; null quando ele já usa a comum.
 */
export function trocaDeCores(id: string, produto: Produto): Uint32Array | null {
  const proprias = PALETAS_PROPRIAS[id]?.[produto];
  if (!proprias) return null;
  const comuns = DEF_PRODUTO[produto].classes;
  const out = new Uint32Array(proprias.length * 2);
  proprias.forEach((cor, i) => {
    out[i * 2] = empacotar(...rgb(cor));
    out[i * 2 + 1] = empacotar(...rgb(comuns[i].cor));
  });
  return out;
}
